import React, { FC, Fragment, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getDetails, GetDetailsParams } from '../../service';
import { PackageDetailsResponse } from '../../ts/interfaces/OphirResponse';
import { faPlaneArrival, faPlaneDeparture } from '@fortawesome/free-solid-svg-icons';
import OphirFlightCard from './OphirFlightCard';
import OphirCarouselCard from './OphirCarouselCard';
import { useRecaptcha } from '@bepro-travel/fe.hooks';
import OphirSummaryCard from './OphirSummaryCard';
import OphirItineraryCard from './OphirItineraryCard';
import OphirIncludeCard from './OphirIncludeCard';
import OphirRoomsCard from './OphirRoomsCard';
import DetailsSkeleton from './OphirDetailsSkeleton';
import { ExpiredToken } from '@bepro-travel/fe.shared';
import { useGetPage } from '../../hooks/useGetPage';
import { useTranslation } from 'react-i18next';

const OphirDetailsPage: FC = () => {
  const [t] = useTranslation() as unknown as [(key: string) => string];
  const { handleRecapcha, isRecaptchaReady } = useRecaptcha();
  const [response, setResponse] = useState<PackageDetailsResponse | undefined>();
  const [urlParams] = useSearchParams();

  useGetPage('rutours/Details');

  useEffect(() => {
    (async () => {
      if (!isRecaptchaReady) return;
      const token = await handleRecapcha();
      if (!token) return;

      const params = Object.fromEntries(urlParams.entries());

      const detailsParams: GetDetailsParams = {
        checkIn: params.checkIn,
        dealId: params.dealId,
        hotelName: params.hotelName,
        nights: +params.nights,
        packageId: params.packageId,
        room: params.room,
        recaptchaToken: token,
        cityCode: params.cityCode,
        isSale: params.isSale === 'true',
      };
      const newDetails = (await getDetails(detailsParams)) as PackageDetailsResponse;
      localStorage.setItem('details', JSON.stringify(newDetails));
      setResponse(newDetails);
    })();
  }, [isRecaptchaReady]); // eslint-disable-line

  return (
    <Fragment>
      {!response?.hasErrors && !!response?.ldsDealDetails && (
        <div className="oph-details-wrapper">
          <div className="oph-details-first mb-20" id='oph-details-first'>
            <OphirCarouselCard
              hotel={response.ldsDealDetails.hotel}
              soldOut={response.ldsDealDetails.status === 'RQ'}
            />
            <OphirSummaryCard details={response.ldsDealDetails} />
          </div>

          <div id='oph-details-flight'>
            <div className="title">{t('flight.informattion_about')} </div>
            <OphirFlightCard
              flight={response.ldsDealDetails.flight.departure}
              returnFlight={response.ldsDealDetails.flight.arrival}
              icon={faPlaneDeparture}
              title={t('flight.departure')}
            />
            <OphirFlightCard
              flight={response.ldsDealDetails.flight.returnDeparture}
              returnFlight={response.ldsDealDetails.flight.returnArrival}
              icon={faPlaneArrival}
              title={t('flight.arrival')}
            />
          </div>
          <OphirItineraryCard itinerary={response.ldsDealDetails.itinerary} details={response.ldsDealDetails} />

          <OphirIncludeCard include={response.ldsDealDetails.includeInfo} />

          <div className="title">{t('tour.choose_rooms')}</div>
          <OphirRoomsCard
            roomsInfo={response.ldsDealDetails.rooms}
            soldOut={response.ldsDealDetails.status === 'RQ'}
            searchToken={response.searchToken}
          />

        </div>
      )}

      {response?.hasErrors && <span>{t('tour.not_found')}</span>}

      {!response && <DetailsSkeleton />}

      {!!response?.sessionExpired && <ExpiredToken />}
    </Fragment>
  );
};

export default OphirDetailsPage;
